/**
 * "What are you adding?" — a whole shop, or one thing at a time.
 *
 * The tiles used to sit permanently on the home screen, which meant the screen
 * for looking at your food was half a form. Behind the + in the dock they're
 * one tap further away and the kitchen gets to be a kitchen.
 *
 * The receipt option sits **above** them, because it is the one that matters
 * most and the tiles are the fallback. Twenty items typed in one at a time is
 * the tax that decides whether anyone is still using this in week three; a
 * receipt is the one artefact that already lists exactly what came into the
 * house, how much of it, and on what day.
 */

import { CategoryTile } from "./CategoryTile";
import { Icon } from "./Icon";
import { Sheet } from "./Sheet";
import { FOOD_CATEGORIES } from "../features/food/types";
import type { FoodCategory } from "../features/food/types";
import { isVisionConfigured } from "../features/vision/config";
import { usePhotoCapture } from "./usePhotoCapture";

import "./CategoryPicker.css";

interface CategoryPickerProps {
  onPick: (category: FoodCategory) => void;
  /**
   * Hands over the *file*, not an encoded photo.
   *
   * The shell owns the encoding because it owns the retry: a receipt refused at
   * one size is re-encoded smaller and sent again, and a data URL can't be
   * re-encoded back up.
   */
  onReceipt: (file: File) => void;
  onError: (message: string) => void;
  onClose: () => void;
}

export function CategoryPicker({
  onPick,
  onReceipt,
  onError,
  onClose,
}: CategoryPickerProps) {
  const { inputRef, isWorking, handleChange, open } = usePhotoCapture({
    onPhoto: () => {},
    onFile: onReceipt,
    onError,
  });

  return (
    <Sheet title="Add something" icon="other" onClose={onClose}>
      {isVisionConfigured() && (
        <>
          <input
            ref={inputRef}
            className="sr-only"
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleChange}
          />
          <button
            className="picker__receipt"
            type="button"
            disabled={isWorking}
            onClick={open}
          >
            <Icon
              name={isWorking ? "spinner" : "camera"}
              className="picker__receipt-icon"
              data-spinning={isWorking || undefined}
            />
            <span className="picker__receipt-text">
              <span className="picker__receipt-title">
                {isWorking ? "Reading…" : "Scan a receipt"}
              </span>
              <span className="picker__receipt-hint">
                A whole shop at once, dated from the receipt
              </span>
            </span>
          </button>

          <p className="picker__or">or one at a time</p>
        </>
      )}

      <div className="picker">
        {FOOD_CATEGORIES.map((category) => (
          <CategoryTile key={category} category={category} onOpen={onPick} />
        ))}
      </div>
    </Sheet>
  );
}
